package io.horion.cv.pages.job.company

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import io.horion.cv.components.cards.CandidatCard
import io.horion.cv.di.koinGet
import io.horion.cv.utils.ApplyJobTestData.getApplicationsByJob
import io.horion.cv.utils.Colors
import io.horion.cv.utils.jobsList
import io.horion.cv.viewModel.JobViewModel
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.cv.components.widgets.ConfigFilterGroup
import io.horion.cv.components.widgets.Language
import io.horion.cv.components.widgets.LanguagesManager
import io.horion.cv.components.widgets.LoadEffect
import io.horion.cv.components.widgets.PaginationControls
import io.horion.cv.models.ApplyJob
import io.horion.cv.models.ApplyJobStatus
import io.horion.cv.models.ExperienceLevel
import io.horion.cv.styles.CreateButtonStyle
import io.horion.cv.utils.Constants.INTER
import io.horion.cv.utils.isDarkTheme
import kotlinx.browser.window
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.vh

@OptIn(DelicateApi::class)
@Page("/job/candidatures")
@Composable
fun Candidature() {

    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark

    val ctx = rememberPageContext()
    val id = ctx.route.params.get("id")
    val breakpoint = rememberBreakpoint()
    val viewModel = koinGet<JobViewModel>()
    val job = jobsList.find { it.id == id }

    Column(
        modifier = Modifier.fillMaxWidth()
            .height(100.vh),
        verticalArrangement = Arrangement.Top,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {

            Header(
                breakpoint = breakpoint, job = job,
                isDark = isDark
            )

            CandidatureContent(
                breakpoint = breakpoint,
                viewModel = viewModel,
                jobId = id,
                isDark = isDark
            )

    }
}

@Composable
fun CandidatureContent(
    breakpoint: Breakpoint,
    viewModel: JobViewModel,
    jobId: String?,
    isDark : Boolean
) {
    val ctx = rememberPageContext()

    var candidatures by remember { mutableStateOf<List<ApplyJob>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    // 🔥 PAGINATION
    var currentPage by remember { mutableStateOf(0) }
    val pageSize = 5

    val totalPages = (candidatures.size + pageSize - 1) / pageSize

    val paginatedCandidatures = candidatures
        .drop(currentPage * pageSize)
        .take(pageSize)

    val currentJob = jobsList.find { it.id == jobId }

    //donnees des competencess
    val competences = currentJob?.requirements ?: emptyList()
    val niveaux = ExperienceLevel.values().toList()
    var selectedLevels by remember { mutableStateOf<Map<String, ExperienceLevel>>(emptyMap()) }


    // 🔄 LOAD DATA
    LaunchedEffect(jobId) {
        if (jobId != null) {
            isLoading = true
            try {
                candidatures = getApplicationsByJob(jobId)
                isLoading = false
            } catch (e: Exception) {
                error = "Erreur lors du chargement: ${e.message}"
                isLoading = false
            }
        }
    }

    // 🔄 RESET PAGE
    LaunchedEffect(candidatures) {
        currentPage = 0
    }

    // 🔥 FILTRE APRÈS PAGINATION
    var pendingCandidatures =
        paginatedCandidatures.filter { it.status == ApplyJobStatus.PENDING }

    var acceptedCandidatures =
        paginatedCandidatures.filter { it.status == ApplyJobStatus.ACCEPTED }

    var rejectedCandidatures =
        paginatedCandidatures.filter { it.status == ApplyJobStatus.REJECTED }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(10.px)
            .backgroundColor(if (isDark) Colors.gray else Colors.LightGray),
        horizontalArrangement = Arrangement.spacedBy(10.px),
        verticalAlignment = Alignment.Top
    ) {

        // ───────── LEFT ─────────
        Box(
            modifier = Modifier
                .backgroundColor(if (isDark) Colors.Primary else Colors.White)
                .flexGrow(3)
                .borderRadius(12.px)
                .padding(15.px)
        ) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {

                // HEADER JOB
                if (currentJob != null) {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .margin(bottom = 20.px)
                            .backgroundColor(Colors.secondary.copy(alpha = 5))
                            .borderRadius(12.px)
                            .padding(15.px)
                    ) {
                        SpanText(
                            text = currentJob.title,
                            modifier = Modifier
                                .fontSize(24.px)
                                .fontWeight(FontWeight.Bold)
                        )
                        SpanText(
                            text = currentJob.enterprise,
                            modifier = Modifier
                                .fontSize(18.px)
                                //.color(Colors.Graytext)
                        )
                        SpanText(
                            text = "Total: ${candidatures.size}",
                            modifier = Modifier
                                .fontSize(16.px)
                                .color(Colors.secondary)
                                .fontWeight(FontWeight.Bold)
                        )
                    }
                }

                // ÉTATS POUR LES FILTRES
                var selectedFilter by remember { mutableStateOf("ALL") } // ALL, ACCEPTED, REJECTED, PENDING

// Données filtrées
                val filteredCandidatures = when (selectedFilter) {
                    "ACCEPTED" -> acceptedCandidatures
                    "REJECTED" -> rejectedCandidatures
                    "PENDING" -> pendingCandidatures
                    else -> candidatures // ALL
                }

// Affichage
                Column(
                    modifier = Modifier.fillMaxWidth()
                ) {
                    // TITRE
                    SpanText(
                        "Gestion des candidatures",
                        modifier = Modifier
                            .fontSize(24.px)
                            .fontWeight(FontWeight.Bold)
                            .margin(bottom = 20.px)
                    )

                    // FILTRES (Onglets)
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .margin(bottom = 20.px),
                        horizontalArrangement = Arrangement.spacedBy(10.px)
                    ) {
                        // Bouton ALL
                        FilterButton(
                            label = "Toutes (${candidatures.size})",
                            isSelected = selectedFilter == "ALL",
                            onClick = { selectedFilter = "ALL" }
                        )

                        // Bouton ACCEPTED
                        FilterButton(
                            label = "Acceptées (${acceptedCandidatures.size})",
                            isSelected = selectedFilter == "ACCEPTED",
                            onClick = { selectedFilter = "ACCEPTED" },
                            color = Colors.secondary
                        )

                        // Bouton REJECTED
                        FilterButton(
                            label = "Rejetées (${rejectedCandidatures.size})",
                            isSelected = selectedFilter == "REJECTED",
                            onClick = { selectedFilter = "REJECTED" },
                            color = Colors.Red
                        )

                        // Bouton PENDING
                        FilterButton(
                            label = "En attente (${pendingCandidatures.size})",
                            isSelected = selectedFilter == "PENDING",
                            onClick = { selectedFilter = "PENDING" },
                            color = Colors.orange
                        )
                    }

                    // LISTE DES CANDIDATURES FILTRÉES
                    if (isLoading) {
                        Box(
                            modifier = Modifier.fillMaxWidth().padding(40.px),
                            contentAlignment = Alignment.Center
                        ) {
                            SpanText("Chargement des candidatures...")
                        }
                        LoadEffect()
                        return@Column
                    }

                    if (error != null) {
                        Box(
                            modifier = Modifier.fillMaxWidth().padding(40.px),
                            contentAlignment = Alignment.Center
                        ) {
                            SpanText(error!!, modifier = Modifier.color(Colors.Red))
                        }
                        return@Column
                    }

                    if (filteredCandidatures.isEmpty()) {
                        Box(
                            modifier = Modifier.fillMaxWidth().padding(40.px),
                            contentAlignment = Alignment.Center
                        ) {
                            Column(
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                Image(
                                    src = "/iconelistevide.png",
                                    modifier = Modifier.size(80.px),
                                    description = "liste vide"
                                )
                                SpanText(
                                    when (selectedFilter) {
                                        "ACCEPTED" -> "Aucune candidature acceptée"
                                         "REJECTED" -> "Aucune candidature rejetée"
                                        "PENDING" -> "Aucune candidature en attente"
                                        else -> "Aucune candidature"
                                    },
                                    modifier = Modifier.margin(top = 10.px).color(Colors.gray)
                                )
                            }
                        }
                    } else {
                        // Affichage des cartes
                        filteredCandidatures.forEach { candidature ->
                            CandidatCard(
                                applyJob = candidature,  // Changé de "candidature" à "applyJob"
                                breakpoint = breakpoint,
                                onStatusChange = { newStatus ->
                                    // Mettre à jour le statut localement
                                    when (newStatus) {
                                        ApplyJobStatus.ACCEPTED -> {
                                            // Déplacer la candidature vers la liste des acceptées
                                            acceptedCandidatures = acceptedCandidatures + candidature
                                            pendingCandidatures = pendingCandidatures - candidature
                                            rejectedCandidatures = rejectedCandidatures - candidature
                                        }
                                        ApplyJobStatus.REJECTED -> {
                                            // Déplacer la candidature vers la liste des rejetées
                                            rejectedCandidatures = rejectedCandidatures + candidature
                                            pendingCandidatures = pendingCandidatures - candidature
                                            acceptedCandidatures = acceptedCandidatures - candidature
                                        }
                                        ApplyJobStatus.PENDING -> {
                                            // Déplacer la candidature vers la liste des en attente
                                            pendingCandidatures = pendingCandidatures + candidature
                                            acceptedCandidatures = acceptedCandidatures - candidature
                                            rejectedCandidatures = rejectedCandidatures - candidature
                                        }
                                    }
                                },
                                onViewCV = { cvUrl ->

                                    window.open(cvUrl, "_blank")
                                },
                                onViewLetter = { letterUrl ->
                                    window.open(letterUrl, "_blank")
                                }
                            )
                            Spacer()
                        }

                        // PAGINATION (seulement si on affiche toutes les candidatures)
                        if (selectedFilter == "ALL" && totalPages > 1) {
                            PaginationControls(
                                currentPage = currentPage,
                                totalPages = totalPages,
                                onPageChange = { currentPage = it },
                                isMobile = breakpoint <= Breakpoint.SM
                            )
                        }
                    }
                }
            }
        }

        // ───────── RIGHT ─────────
        Box(
            modifier = Modifier
                .backgroundColor(if (isDark) Colors.Primary else Colors.White)
                .width(500.px)
                //.flexGrow(1)
                .position(Position.Sticky)
                .top(80.px)
                .borderRadius(12.px)
                .padding(15.px)
        ) {
            Column (modifier = Modifier.fillMaxWidth()){
                SpanText(
                    "Configurer le tri des candidats",
                    modifier = Modifier
                        .fontSize(24.px)
                        .fontWeight(FontWeight.Bold)
                )
                //SEPARATEUR
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(1.px)
                        .backgroundColor(Colors.LightGray)
                        .margin(topBottom = 15.px)
                )

                //les competences
                ConfigFilterGroup(
                    title = "Compétences",
                    items = competences,
                    selectOptions = niveaux,
                    selectedValues = selectedLevels,
                    onSelectionChange = { competence, niveau ->
                        selectedLevels = selectedLevels.toMutableMap().apply {
                            put(competence, niveau)
                        }
                    },
                    labelItem = { it },
                    labelOption = { it.getDisplayName() }, // Important: convertir l'enum en texte
                    breakpoint = breakpoint
                )
                //LANGUES
                var languages by remember { mutableStateOf<List<Language>>(emptyList()) }

                LanguagesManager(
                    title = "Langues",
                    languages = languages,
                    onLanguagesChange = { newLanguages ->
                        languages = newLanguages
                    },
                    breakpoint = breakpoint
                )

                Button(
                    onClick = {

                    },
                    modifier = CreateButtonStyle.toModifier()
                        .margin(right = 5.px)
                        .width(if (breakpoint <= Breakpoint.MD) 80.px else 120.px)
                        .height(if (breakpoint <= Breakpoint.MD) 30.px else 46.px)
                        .fontFamily(INTER)
                        .fontSize(if (breakpoint <= Breakpoint.MD) 12.px else 16.px)
                        .fontWeight(FontWeight.SemiBold)
                        .borderRadius(7.px)
                        .border(2.px, LineStyle.Solid, Colors.Blue)
                        .color(Colors.secondary),
                ) {
                    SpanText(if (breakpoint <= Breakpoint.MD) "Trier" else "Lancer le tri")
                }
            }
        }
    }
}

// Composant pour les boutons de filtre
@Composable
fun FilterButton(
    label: String,
    isSelected: Boolean,
    onClick: () -> Unit,
    color: CSSColorValue = Colors.Primary
) {
    Button(
        onClick = { onClick() },
        modifier = Modifier
            .flexGrow(1)
            .borderRadius(8.px)
            .backgroundColor(
                if (isSelected) color else Colors.White
            )
            .border(
                1.px,
                LineStyle.Solid,
                if (isSelected) color else Colors.LightGray
            )
    ) {
        SpanText(
            label,
            modifier = Modifier.color(
                if (isSelected) Colors.White else Colors.DarkGray
            )
        )
    }
}

